import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataTestAttributes } from '../../constants/DataTestAttributes';
import { UpiFormContainer, UpiFormHeader, UpiFormInputs } from './styled';
import { getString } from '../../constants/Strings';
import { StyledRequiredFieldsLegend } from '../PaymentForm/styled';
import { NAME_LENGTH, UPIFIELDS } from './types';
import { useAppSelector } from '../../hooks/useAppSelector';
import { configSelector } from '../../redux/ConfigState/ConfigState.selectors';
import { UserAddressCommandNames, ValidationManager } from '@chegg/commerce-utils';
import { ElementType, sendRioEvent, SpecType, SubType } from '@chegg/commerce-utils';
import { sendRioEventCallback } from '../../utils/CallbackUtils';
import { submitUpiForm } from './submitForm';
import { paymentSelector } from '../../redux/PaymentState/PaymentState.selectors';
import { sendErrorRioEvent } from './sendErrorRioEvent';
import BillDeskSDK from '../../business/processors/billdesk/Billdesk';
import { FloatingInput } from '@chegg-ui/input';
import { getPaymentFieldLabelText, getPaymentFieldLabelErrorText } from '../PaymentForm/helpers';
import { PaymentFormDataFields, PaymentFormValidationFields } from '../UserAddress/constants';
import UserAddress from '../UserAddress';
import { mopOnFileSelector } from '../../redux/MopOnFileState/MopOnFileState.selectors';
import { saveUserAddressCommands } from '../../redux/CommandState/CommandState.actions';
var VALIDATION_TYPES = ValidationManager.types.VALIDATION_TYPES;
export var defaultFormValues = _defineProperty({}, UPIFIELDS.NAME, {
  value: '',
  error: false
});

/**
 * This component renders the UPI form and its local state management
 */
var UpiForm = function UpiForm() {
  var _useState = useState(_defineProperty(_defineProperty({}, UPIFIELDS.NAME, true), UPIFIELDS.PINCODE, true)),
    _useState2 = _slicedToArray(_useState, 2),
    shouldSendInteractionEvent = _useState2[0],
    setShouldSendInteractionEvent = _useState2[1];
  var config = useAppSelector(configSelector);
  var payment = useAppSelector(paymentSelector);
  var _useAppSelector = useAppSelector(mopOnFileSelector),
    selectedMop = _useAppSelector.selectedMop;
  var submit = config.submit,
    rioNamespace = config.rioNamespace,
    paymentForm = config.paymentForm,
    ui = config.ui;
  var dispatch = useDispatch();
  var isDisabled = ui === null || ui === void 0 ? void 0 : ui.showUpiProcessing;

  // local state for managing the form
  var _useState4 = useState(defaultFormValues),
    _useState5 = _slicedToArray(_useState4, 2),
    formValues = _useState5[0],
    setFormValues = _useState5[1];

  // this effect will trigger config change for UserAddress component, to either disable or enable it for UPI
  useEffect(function () {
    dispatch(saveUserAddressCommands([{
      commandName: UserAddressCommandNames.SAVE_CONFIGS,
      data: {
        inputDisabled: isDisabled
      }
    }]));
  }, [isDisabled, dispatch]);

  // this effect will submit the form is external submit was triggered ({ state.config.submit == true })
  useEffect(function () {
    if (submit) {
      submitUpiForm({
        dispatch: dispatch,
        payload: formValues,
        payment: payment,
        setField: setFormField
      });
    }
  }, [submit]);

  // Callbacks ...
  // ----------------
  // `setFormField` sets local form values (& errors)
  var setFormField = useCallback(function (field, payload) {
    setFormValues(function (formValues) {
      return _objectSpread(_objectSpread({}, formValues), {}, _defineProperty({}, field, _objectSpread(_objectSpread({}, formValues[field]), payload)));
    });
  }, [setFormValues]);
  // ----------------
  // `changeField` onChange callback factory (for each field)
  var changeField = useCallback(function (field) {
    return function (value) {
      setFormField(field, {
        value: value
      });
      if (shouldSendInteractionEvent[field]) {
        sendTypedRioEvent(field);
      }
    };
  }, [setFormField, shouldSendInteractionEvent]);
  // ----------------
  // `submitForm` onSubmit form callback
  var submitForm = useCallback(function (e) {
    e.preventDefault();
    submitUpiForm({
      dispatch: dispatch,
      payload: formValues,
      payment: payment,
      setField: setFormField
    });
  }, [dispatch, formValues, setFormField, payment, submitUpiForm]);
  // ----------------
  // `validateName` name validation callback
  var validateName = useCallback(function () {
    var validName = validate(UPIFIELDS.NAME, VALIDATION_TYPES.ALPHA);
    if (!validName) return;
    var error = true;
    var value = formValues[UPIFIELDS.NAME].value;
    if (value.length <= NAME_LENGTH) {
      error = false;
    } else {
      sendErrorRioEvent(UPIFIELDS.NAME, rioNamespace);
    }
    setFormField(UPIFIELDS.NAME, {
      error: error
    });
  }, [formValues, setFormField, validate, rioNamespace]);
  // ----------------
  // `getError` gets error from state for a specified field
  var getError = useCallback(function (field) {
    if (formValues[field].error) {
      switch (field) {
        case 'name':
          {
            return getPaymentFieldLabelErrorText(paymentForm, PaymentFormValidationFields.INVALID_NAME) || getString('APP.UPI.FORM.ERROR.NAME');
            break;
          }
      }
      return '';
    }
    return;
  }, [formValues]);

  // ----------------
  // sends appropriate rio events according to the field
  var sendTypedRioEvent = useCallback(function (field) {
    var info = '';
    if (field === UPIFIELDS.NAME) {
      info = getString('APP.RIO.EVENTS.EL_NAME.UPI_NAME_FIELD_TYPED');
    }
    setShouldSendInteractionEvent(_objectSpread(_objectSpread({}, shouldSendInteractionEvent), {}, _defineProperty({}, field, false)));
    sendRioEvent({
      info: info,
      force: false,
      subType: SubType.TYPED,
      namespace: rioNamespace,
      elementType: ElementType.TEXT,
      specType: SpecType.INTERACTION,
      callback: sendRioEventCallback
    });
  }, [rioNamespace, shouldSendInteractionEvent]);
  // End of Callbacks

  return /*#__PURE__*/React.createElement(UpiFormContainer, {
    id: 'upi-payment-form',
    "data-test": DataTestAttributes.UPI_FORM,
    onSubmit: submitForm
  }, /*#__PURE__*/React.createElement(UpiFormHeader, {
    "data-test": DataTestAttributes.UPI_FORM_HEADER
  }, getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.PAY_WITH_UPI) || getString('APP.UPI.FORM.HEADER.PAY')), /*#__PURE__*/React.createElement(UpiFormInputs, null, /*#__PURE__*/React.createElement(StyledRequiredFieldsLegend, null, getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.ALL_REQUIRED_FIELDS) || getString('APP.FORM.LEGEND.REQUIRED_FIELDS')), /*#__PURE__*/React.createElement("fieldset", null, /*#__PURE__*/React.createElement(FloatingInput, {
    inputId: UPIFIELDS.NAME,
    type: "text",
    onChange: changeField(UPIFIELDS.NAME),
    onBlur: validateName,
    "data-test": DataTestAttributes.UPI_FORM_NAME,
    width: '100%',
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.NAME) || getString('APP.UPI.FORM.LABEL.NAME'),
    errorText: getError(UPIFIELDS.NAME),
    visuallyHideLabel: false,
    value: formValues.name.value,
    disabled: isDisabled
  })), /*#__PURE__*/React.createElement("fieldset", null, /*#__PURE__*/React.createElement(UserAddress, {
    isUpi: true,
    mop: selectedMop
  }))), /*#__PURE__*/React.createElement(BillDeskSDK, null));
  function validate(field, validation) {
    if (!shouldSendInteractionEvent[field]) {
      setShouldSendInteractionEvent(function (shouldSendInteractionEvent) {
        return _objectSpread(_objectSpread({}, shouldSendInteractionEvent), {}, _defineProperty({}, field, true));
      });
    }
    var error = ValidationManager.validateField({
      fieldName: field,
      value: formValues[field].value,
      validations: [VALIDATION_TYPES.REQUIRED, validation]
    }).error !== '';
    if (error) {
      sendErrorRioEvent(field, rioNamespace);
    }
    setFormField(field, {
      error: error
    });
    return !error;
  }
};
export default UpiForm;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/internals/UpiOption/UpiForm.js
// module id = 4591
// module chunks = 11